// @flow
import xhrRequest from 'app/packages/api/xhrRequest';
import fetchRequest, { keepaliveIsSupported } from 'app/packages/api/fetchRequest';
import type { Config, AjaxHttpResponse } from 'app/packages/api/types';

type PlatformConfig = {|
  csrf: {
    isRequired: (method: string, url: string) => boolean,
    errorIfMissing: boolean,
    getToken: () => ?string,
  },
  getAppVersion: () => string,
  appendQuery: (url: string, params: Object) => string,
  buildQuery: (obj: Object) => string,
|};

export type Options = $Shape<Config>;

export default function ajax(
  options: Options,
  { csrf, getAppVersion, appendQuery, buildQuery }: PlatformConfig,
): {| promise: Promise<AjaxHttpResponse>, abort: () => void |} {
  const config = {
    async: true,
    cache: false,
    data: undefined,
    headers: {},
    keepalive: false,
    query: {},
    timeout: 0,
    type: 'GET',
    ...options,
  };

  let { url, data } = config;
  const method = config.type;

  // Manage query params and form data
  if (data && typeof data === 'object') {
    if (method === 'GET') {
      url = appendQuery(url, data);
      data = undefined;
    } else if (!(data instanceof FormData)) {
      data = buildQuery(data);
    }
  }

  // Add cache busting query param
  if (config.cache === false && method === 'GET') {
    url = appendQuery(url, { _: Date.now() });
  }

  const csrfTokenIsRequired = csrf.isRequired(method, url);
  if (csrf.errorIfMissing && csrfTokenIsRequired && !csrf.getToken()) {
    // if the request needs a csrf token and we don't have one
    // error and early return
    // This is likely because the user has cookies disabled
    return {
      promise: Promise.reject({ type: 'csrferror', message: 'No csrf token found' }),
      abort: () => {},
    };
  }

  const request = config.keepalive && keepaliveIsSupported() ? fetchRequest : xhrRequest;

  return request({
    ...config,
    url,
    data,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json, text/javascript, */*, q=0.01',
      'X-APP-VERSION': getAppVersion(),
      ...(method !== 'GET' && data && !(data instanceof FormData)
        ? { 'Content-Type': 'application/x-www-form-urlencoded' }
        : {}),
      ...(csrfTokenIsRequired ? { 'X-CSRFToken': csrf.getToken() || '' } : {}),
      ...config.headers,
    },
  });
}
